<template>
  <v-expansion-panel @change="onChange">
    <v-expansion-panel-header>
      <v-row no-gutters class="align-center">
        <!-- Icon -->
        <v-col cols="2">
          <!-- display: block -->
          <v-badge
            bordered
            overlap
            :color="hasAlert ? 'error' : 'warning'"
            icon="mdi-exclamation-thick"
            :value="!isAlarmOrException && (hasAlert || hasWarning)"
          >
            <v-btn
              fab
              small
              :to="`/individual/${encodeURIComponent(individual.id.value)}`"
              :color="isAlarmOrException && hasAlert ? 'error' : 'secondary'"
            >
              <v-icon>{{ recTypeIcon }}</v-icon>
            </v-btn>
          </v-badge>
        </v-col>

        <!-- Type -->
        <v-col cols="2" class="text-body-1 text-truncate font-weight-medium">
          <!-- display: block -->
          <iri-value
            :iri="individual.type"
            :highlights="highlights"
          ></iri-value>
        </v-col>

        <!-- Label -->
        <v-col cols="4" class="text-body-1 text-truncate">
          <!-- display: block -->
          <span>
            <highlighted-text
              :value="individual.label.value"
              :highlights="highlights"
            ></highlighted-text>
          </span>
        </v-col>

        <!-- Id -->
        <v-col cols="4" class="text-body-1 text-truncate text-right">
          <iri-value
            :iri="individual.id"
            :highlights="highlights"
            :link="true"
          ></iri-value>
        </v-col>
      </v-row>
    </v-expansion-panel-header>

    <v-expansion-panel-content class="pt-4">
      <v-row no-gutters class="align-center">
        <!-- display: block -->
        <v-col cols="2"></v-col>
        <v-col cols="10">
          <data-props
            :dataProperties="dataProperties"
            :highlights="highlights"
          ></data-props></v-col
      ></v-row>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import Vue from 'vue'

import { Iri, DataProperty } from '@/rec-cache/rec-cache-types'
import { RecIndividual } from '@/rec-cache/rec-cache-individual'
import { recIcon } from '@/vue-wob-cache/vue-wob-icons'

import HighlightedText from './HighlightedText.vue'
import IriValue from './IriValue.vue'
// import DataProp from './DataProperty.vue'
import DataProps from './DataProperties.vue'

import { upProperties, downProperties } from '@/rec-cache/rec-cache-hierarchies'
import { Alarms, ExceptionEvent } from '@/vue-wob-cache/vue-wob-alarms'

export default {
  components: { HighlightedText, IriValue, DataProps /* DataProp */ },
  component: 'IndividualListItem',
  props: {
    id: {
      type: String,
    },
    highlights: {
      type: Array,
    },
  },
  computed: {
    dataProperties: function () {
      return this.individual.properties.filter((p) => p instanceof DataProperty)
    },
    recTypeIcon: function () {
      return recIcon(this.individual.type, this.hasAlert)
    },
    inHierarchy: function () {
      return this.realEstate?.isDefined
    },
    hasAlert: function () {
      const id = this.individual.id.value
      return (
        this.$wob.openExceptions.ids.map((id) => id.value).includes(id) ||
        this.$wob.activeAlarms.ids.map((id) => id.value).includes(id) ||
        this.$wob.alarmingAssets.ids.map((id) => id.value).includes(id)
      )
    },
    hasWarning: function () {
      return this.$wob.impactedAssets.ids
        .map((id) => id.value)
        .includes(this.individual.id.value)
    },
    isAlarmOrException: function () {
      return (
        Alarms.includes(this.individual.type?.value) ||
        ExceptionEvent == this.individual.type?.value
      )
    },
    // TODO refactor/remove after testing
    // isAlarm: function () {
    //   // The type is not set at initial render, hence optional chaining.
    //   return Alarms.includes(this.individual.type?.value)
    // },
    // hasException: function () {
    //   return this.$wob.exceptions.ids.includes(this.id)
    // },
  },
  data: () => ({
    individual: new RecIndividual(),
    path: [],
    // TODO cache path varibale purpose unclear. Seems to help find real estate. Which then may be linked. See commented code.
    cachePath: true,
    // TODO may contain not defined individual, a real estate individual or null...
    realEstate: new RecIndividual(),
  }),
  watch: {
    id(newId) {
      // console.log('components.individual-list-item.watch.id', newId)

      this.individual = Vue.observable(this.$wob.read(new Iri(newId), true))
      // console.log(
      //   'components.individual-list-item.watch.individual',
      //   this.individual
      // )
    },
  },
  mounted() {
    // Read individual from cache.
    // console.log(
    //   'components.individual-list-item.mounted',
    //   this.id,
    //   this.highlights
    // )

    this.individual = Vue.observable(this.$wob.read(new Iri(this.id)))
    // console.log(
    //   'components.individual-list-item.mounted.individual',
    //   this.individual
    // )
  },
  methods: {
    onChange: function () {
      console.log('components.individual-list-item.on-change')
      if (this.cachePath) {
        console.time('path')
        this.cachePath = false
        this.path = this.traverseUp(this.individual.id)
        console.log('components.individual-list-item.on-change.path', this.path)
        // TODO use an Iri constant for real estate...
        this.realEstate = this.path
          .map((i) => this.$wob.read(i))
          .find((i) => i.type.value == 'https://w3id.org/rec/core/RealEstate')
        console.timeEnd('path')
      }
    },

    // Recursive traversal upwards. Parameter is Iri. Returns an array of Iris.
    traverseUp: function (id) {
      const path = [id]
      console.log('components.individual-list-item.traverse-up.path', path)
      const upEdges = this.$wob
        .queryEdges({ source: id.value })
        .filter((e) => upProperties.includes(e.label.value))
      const downEdges = this.$wob
        .queryEdges({ target: id.value })
        .filter((e) => downProperties.includes(e.label.value))

      console.log(
        'components.individual-list-item.traverse-up.up-edges',
        upEdges
      )
      console.log(
        'components.individual-list-item.traverse-up.down-edges',
        downEdges
      )

      path.push(
        ...upEdges
          .map((e) => this.traverseUp(e.target))
          .reduce((previous, current) => previous.concat(current), [])
      )

      path.push(
        ...downEdges
          .map((e) => this.traverseUp(e.source))
          .reduce((previous, current) => previous.concat(current), [])
      )

      return path
    },
  },
}
</script>
